/**
 * Design tokens for the Builder canvas.
 *
 * This is a deliberate MIRROR of
 * `packages/website-blocks/src/theme/design-tokens.ts` and
 * `button-tokens.ts` in the `zoea-website-module` repository.
 *
 * Why a mirror rather than a shared package
 * -----------------------------------------
 * The two repositories are independent by architectural decision (ADR-001,
 * ADR-003): the Website Module is a standalone API-only renderer and the
 * Platform never depends on it. A shared npm package would couple their
 * release cycles, and publishing one to bridge two private repos is a
 * heavier commitment than this problem warrants today.
 *
 * The cost of that choice is drift, so it is paid for explicitly:
 * `tokens.parity.test.ts` pins every value here against the same golden
 * table the Website Module's own tests use. If a scale changes on one side
 * and not the other, that test fails and names the token. The mirror is
 * allowed to exist because it cannot silently diverge -- not because
 * duplication is fine.
 *
 * When a token changes: change it in BOTH files and in the golden table.
 */
import type {
  ButtonPresentation,
  ColorRole,
  ColumnLayout,
  HoverEffect,
  RadiusLevel,
  SectionStyle,
  ShadowLevel,
  SiteChrome,
  SurfaceEffects,
  VisualSurface,
} from '../types';

type Theme = SiteChrome['theme'];

// ---------------------------------------------------------------------------
// Scales (mirror)
// ---------------------------------------------------------------------------

const FONT_STACKS: Record<string, { heading: string; body: string }> = {
  modern: {
    heading: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
    body: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
  },
  humanist: {
    heading: '"Optima", "Segoe UI", Candara, "Gill Sans", system-ui, sans-serif',
    body: '"Segoe UI", Candara, system-ui, -apple-system, sans-serif',
  },
  editorial: {
    heading: 'Georgia, "Iowan Old Style", "Times New Roman", serif',
    body: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
  },
  classic: {
    heading: '"Palatino Linotype", Palatino, Georgia, "Book Antiqua", serif',
    body: 'Georgia, "Iowan Old Style", "Times New Roman", serif',
  },
};

const HEADING_SCALES: Record<string, Record<string, string>> = {
  // Site canvases are query containers. Container-relative units keep the
  // same theme readable in the 390px mobile canvas as on desktop; viewport
  // units incorrectly sized mobile previews from the browser window.
  sm: { h1: 'clamp(1.9rem, 1.4rem + 2.2cqw, 2.8rem)', h2: 'clamp(1.5rem, 1.2rem + 1.4cqw, 2.1rem)', h3: 'clamp(1.2rem, 1.1rem + 0.6cqw, 1.5rem)' },
  md: { h1: 'clamp(2.4rem, 1.7rem + 3.2cqw, 4rem)', h2: 'clamp(1.8rem, 1.4rem + 1.9cqw, 2.7rem)', h3: 'clamp(1.3rem, 1.15rem + 0.8cqw, 1.7rem)' },
  lg: { h1: 'clamp(2.9rem, 1.9rem + 4.4cqw, 5.2rem)', h2: 'clamp(2.1rem, 1.6rem + 2.5cqw, 3.3rem)', h3: 'clamp(1.45rem, 1.2rem + 1.1cqw, 1.95rem)' },
};

const BODY_SCALES: Record<string, string> = { sm: '0.95rem', md: '1.05rem', lg: '1.18rem' };

const RADII: Record<string, string> = {
  sharp: '0px', subtle: '6px', rounded: '14px', soft: '26px', pill: '999px',
};

const SHADOWS: Record<string, string> = {
  none: 'none',
  subtle: '0 1px 2px rgba(15, 23, 42, 0.06), 0 2px 8px rgba(15, 23, 42, 0.06)',
  medium: '0 2px 6px rgba(15, 23, 42, 0.08), 0 10px 24px rgba(15, 23, 42, 0.10)',
  strong: '0 6px 16px rgba(15, 23, 42, 0.12), 0 24px 56px rgba(15, 23, 42, 0.18)',
};

/** The theme-level shadow scale, which predates the four-level element scale
 * and uses different names. Kept distinct on purpose: renaming it would
 * change every stored theme. */
const THEME_SHADOWS: Record<string, string> = {
  none: 'none',
  subtle: '0 1px 2px rgba(15, 23, 42, 0.06), 0 4px 12px rgba(15, 23, 42, 0.05)',
  elevated: '0 4px 10px rgba(15, 23, 42, 0.08), 0 18px 40px rgba(15, 23, 42, 0.12)',
};

const CONTENT_WIDTHS: Record<string, string> = { compact: '62rem', normal: '76rem', wide: '88rem' };

const SECTION_SPACING: Record<string, string> = {
  compact: 'clamp(1.75rem, 1.2rem + 2cqw, 3rem)',
  normal: 'clamp(3rem, 2rem + 4cqw, 5.5rem)',
  spacious: 'clamp(4.5rem, 3rem + 6cqw, 8rem)',
  extra: 'clamp(6rem, 3.5rem + 9cqw, 11rem)',
};

const DENSITY_MULTIPLIER: Record<string, number> = { compact: 0.8, normal: 1, spacious: 1.25 };

const MIN_HEIGHTS: Record<string, string> = {
  auto: 'auto',
  medium: 'clamp(22rem, 45vh, 32rem)',
  tall: 'clamp(30rem, 68vh, 46rem)',
  screen: '100svh',
};

const BUTTON_PADDING: Record<string, { y: string; x: string; font: string }> = {
  small: { y: '0.45rem', x: '0.95rem', font: '0.82rem' },
  normal: { y: '0.7rem', x: '1.4rem', font: '0.95rem' },
  large: { y: '0.95rem', x: '2rem', font: '1.05rem' },
};

export const TOKEN_SCALES = {
  FONT_STACKS, HEADING_SCALES, BODY_SCALES, RADII, SHADOWS, THEME_SHADOWS,
  CONTENT_WIDTHS, SECTION_SPACING, DENSITY_MULTIPLIER, MIN_HEIGHTS, BUTTON_PADDING,
} as const;

// ---------------------------------------------------------------------------
// Colour
// ---------------------------------------------------------------------------

export type ResolvedPalette = Record<ColorRole, string>;

export function resolvePalette(theme: Theme): ResolvedPalette {
  return { ...theme.colors, light: '#ffffff' } as ResolvedPalette;
}

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/** Resolve a palette role or an explicitly validated colour override. */
export function resolveColorValue(
  palette: ResolvedPalette,
  role: ColorRole | undefined,
  customColor: string | null | undefined,
  fallback: ColorRole,
): string {
  return customColor && HEX_COLOR_PATTERN.test(customColor) ? customColor : palette[role ?? fallback];
}

export function relativeLuminance(hex: string): number {
  const value = hex.replace('#', '');
  const channels = [0, 2, 4].map((offset) => {
    const channel = parseInt(value.slice(offset, offset + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0]! + 0.7152 * channels[1]! + 0.0722 * channels[2]!;
}

export function contrastRatio(a: string, b: string): number {
  const [lighter, darker] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (lighter! + 0.05) / (darker! + 0.05);
}

const INK_DARK = '#0f172a';
const INK_LIGHT = '#ffffff';

/** Computed, not thresholded -- see the Website Module's note: the crossover
 * is nowhere near the luminance midpoint and depends on how dark the ink is. */
export function readableTextOn(background: string): string {
  return contrastRatio(background, INK_DARK) >= contrastRatio(background, INK_LIGHT) ? INK_DARK : INK_LIGHT;
}

export function hexToRgba(hex: string, alpha: number): string {
  const value = hex.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha)).toFixed(3)})`;
}

// ---------------------------------------------------------------------------
// Site tokens
// ---------------------------------------------------------------------------

export function siteTokens(theme: Theme): Record<string, string> {
  const palette = resolvePalette(theme);
  const fonts = FONT_STACKS[theme.typography.pairing] ?? FONT_STACKS.modern!;
  const headings = HEADING_SCALES[theme.typography.headingScale] ?? HEADING_SCALES.md!;
  const density = DENSITY_MULTIPLIER[theme.layout.density] ?? 1;

  return {
    '--zs-color-primary': palette.primary,
    '--zs-color-secondary': palette.secondary,
    '--zs-color-accent': palette.accent,
    '--zs-color-background': palette.background,
    '--zs-color-surface': palette.surface,
    '--zs-color-text': palette.text,
    '--zs-color-muted': palette.muted,
    '--zs-color-dark': palette.dark,
    '--zs-color-light': palette.light,
    '--zs-on-primary': readableTextOn(palette.primary),
    '--zs-on-accent': readableTextOn(palette.accent),
    '--zs-on-dark': readableTextOn(palette.dark),
    '--zs-font-heading': fonts.heading,
    '--zs-font-body': fonts.body,
    '--zs-text-h1': headings.h1!,
    '--zs-text-h2': headings.h2!,
    '--zs-text-h3': headings.h3!,
    '--zs-text-body': BODY_SCALES[theme.typography.bodyScale] ?? BODY_SCALES.md!,
    '--zs-radius': RADII[theme.shapes.radius] ?? RADII.subtle!,
    '--zs-radius-image': RADII[theme.shapes.imageRadius] ?? RADII.subtle!,
    '--zs-radius-button': RADII[theme.buttons.radius] ?? RADII.subtle!,
    '--zs-shadow': THEME_SHADOWS[theme.shapes.shadow] ?? THEME_SHADOWS.subtle!,
    '--zs-content-width': CONTENT_WIDTHS[theme.layout.contentWidth] ?? CONTENT_WIDTHS.normal!,
    '--zs-density': String(density),
    '--zs-button-padding-y': theme.buttons.size === 'large' ? '0.95rem' : '0.7rem',
    '--zs-button-padding-x': theme.buttons.size === 'large' ? '2rem' : '1.4rem',
  };
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

const GRADIENT_DIRECTIONS: Record<string, string> = {
  'to-bottom': 'to bottom',
  'to-right': 'to right',
  'to-bottom-right': '135deg',
  radial: 'radial',
};

export interface ResolvedSectionStyle {
  variables: Record<string, string>;
  background: string;
  isDark: boolean;
  minHeight: string;
  paddingBlock: string;
  paddingTop: string;
  maxWidth: string;
  textAlign: string;
  justifyContent: string;
}

export function resolveSectionStyle(
  theme: Theme,
  style: SectionStyle | undefined,
  imageUrl?: string | null,
): ResolvedSectionStyle {
  const palette = resolvePalette(theme);
  const resolved = style ?? {};
  const background = resolved.background ?? {};
  const density = DENSITY_MULTIPLIER[theme.layout.density] ?? 1;
  const basePadding = SECTION_SPACING[resolved.spacing ?? 'normal'] ?? SECTION_SPACING.normal!;
  const paddingBlock = `calc(${basePadding} * ${density})`;
  const compactTopPadding = `calc(${SECTION_SPACING.compact} * ${density})`;

  let paint = 'transparent';
  if (background.mode === 'solid') {
    paint = background.legacyColor ?? resolveColorValue(palette, background.color, background.customColor, 'surface');
  } else if (background.mode === 'gradient') {
    const from = resolveColorValue(palette, background.gradientFrom, background.gradientFromCustom, 'primary');
    const to = resolveColorValue(palette, background.gradientTo, background.gradientToCustom, 'dark');
    const direction = GRADIENT_DIRECTIONS[background.gradientDirection ?? 'to-bottom-right'] ?? '135deg';
    paint = direction === 'radial'
      ? `radial-gradient(circle at 50% 0%, ${from}, ${to})`
      : `linear-gradient(${direction}, ${from}, ${to})`;
  } else if (background.mode === 'image' && imageUrl) {
    const overlayColor = resolveColorValue(palette, background.overlay, background.overlayCustom, 'dark');
    const overlay = hexToRgba(overlayColor, (background.overlayIntensity ?? 45) / 100);
    paint = `linear-gradient(${overlay}, ${overlay}), url("${encodeURI(imageUrl)}")`;
  }

  const widthMode = resolved.widthMode ?? 'content';
  const verticalAlign = resolved.verticalAlign ?? 'center';

  return {
    variables: {
      ...(resolved.colorScheme === 'dark'
        ? {
            '--zs-color-text': '#ffffff',
            '--zs-color-muted': 'rgba(255, 255, 255, 0.72)',
            '--zs-color-surface': 'rgba(255, 255, 255, 0.08)',
          }
        : {}),
      ...(resolved.textColor && HEX_COLOR_PATTERN.test(resolved.textColor)
        ? { '--zs-section-text': resolved.textColor, '--zs-color-text': resolved.textColor }
        : {}),
    },
    background: paint,
    isDark: resolved.colorScheme === 'dark',
    minHeight: MIN_HEIGHTS[resolved.minHeight ?? 'auto'] ?? 'auto',
    paddingBlock,
    paddingTop: resolved.topSpacing === 'flush' ? '0px' : resolved.topSpacing === 'compact' ? compactTopPadding : paddingBlock,
    maxWidth: widthMode === 'full' ? '100%' : widthMode === 'wide' ? CONTENT_WIDTHS.wide! : 'var(--zs-content-width)',
    textAlign: resolved.textAlign ?? 'left',
    justifyContent: verticalAlign === 'top' ? 'flex-start' : verticalAlign === 'bottom' ? 'flex-end' : 'center',
  };
}

// ---------------------------------------------------------------------------
// Buttons and surfaces
// ---------------------------------------------------------------------------

export function shadowValue(level: ShadowLevel): string { return SHADOWS[level] ?? SHADOWS.none!; }
export function radiusValue(level: RadiusLevel): string { return RADII[level] ?? RADII.subtle!; }

export interface ResolvedButton {
  variables: Record<string, string>;
  styleName: string;
  hoverName: HoverEffect;
  fullWidth: boolean;
  iconName: string | null;
  iconPosition: 'left' | 'right';
}

/** Canonical bridge for legacy block.variant button data. */
export function legacyButtonPresentation(block: { button?: ButtonPresentation | null; variant?: string }): ButtonPresentation | undefined {
  if (block.button) return block.button;
  return block.variant === 'primary' || block.variant === 'secondary' || block.variant === 'accent' || block.variant === 'outline' || block.variant === 'ghost' || block.variant === 'text'
    ? { style: block.variant }
    : undefined;
}

export function resolveButton(theme: Theme, presentation: ButtonPresentation | undefined): ResolvedButton {
  const config = presentation ?? {};
  const palette = resolvePalette(theme);
  const padding = BUTTON_PADDING[config.size ?? 'normal'] ?? BUTTON_PADDING.normal!;
  const fallbackColor: ColorRole = config.style === 'secondary'
    ? 'secondary'
    : config.style === 'accent'
      ? 'accent'
      : 'primary';
  const fill = resolveColorValue(palette, config.color ?? undefined, config.customColor, fallbackColor);

  return {
    variables: {
      '--zs-btn-fill': fill,
      '--zs-btn-ink': readableTextOn(fill),
      '--zs-btn-line': fill,
      '--zs-btn-radius': radiusValue((config.radius ?? theme.buttons.radius) as RadiusLevel),
      '--zs-btn-shadow': shadowValue(config.shadow ?? 'none'),
      '--zs-btn-pad-y': padding.y,
      '--zs-btn-pad-x': padding.x,
      '--zs-btn-font': padding.font,
    },
    styleName: config.style ?? 'primary',
    hoverName: config.hover ?? 'none',
    fullWidth: config.width === 'full',
    iconName: !config.icon || config.icon === 'none' ? null : config.icon,
    iconPosition: config.iconPosition ?? 'right',
  };
}

export function resolveSurface(theme: Theme, effects: SurfaceEffects | undefined): Record<string, string> {
  const config = effects ?? {};
  const palette = resolvePalette(theme);
  const border = config.border ?? 'none';
  const borderColor = border === 'accent'
    ? palette.accent
    : border === 'strong'
      ? 'currentColor'
      : 'rgba(100, 116, 139, 0.28)';

  return {
    '--zs-surface-shadow': shadowValue(config.shadow ?? 'none'),
    '--zs-surface-radius': radiusValue(config.radius ?? 'subtle'),
    '--zs-surface-border-width': border === 'none' ? '0px' : border === 'strong' ? '2px' : '1px',
    '--zs-surface-border-color': borderColor,
  };
}

/** Mirror of `@zoea-website/website-blocks::resolveVisualSurface`. */
export function resolveVisualSurface(theme: Theme, surface: VisualSurface): Record<string, string> {
  const palette = resolvePalette(theme);
  const custom = resolveColorValue(palette, surface.fillColor, surface.customColor, 'primary');
  const direction = surface.gradientDirection === 'radial'
    ? 'radial-gradient(circle at 50% 50%, VAR_FROM, VAR_TO)'
    : `linear-gradient(${surface.gradientDirection === 'to-bottom' ? 'to bottom' : surface.gradientDirection === 'to-right' ? 'to right' : '135deg'}, VAR_FROM, VAR_TO)`;
  const background = surface.fillMode === 'gradient'
    ? direction
      .replace('VAR_FROM', resolveColorValue(palette, surface.gradientFrom, surface.gradientFromCustom, 'primary'))
      .replace('VAR_TO', resolveColorValue(palette, surface.gradientTo, surface.gradientToCustom, 'accent'))
    : surface.fillMode === 'custom' ? custom : palette[surface.fillColor ?? 'primary'];
  const radius = surface.shape === 'ellipse'
    ? '50%'
    : surface.shape === 'blob'
      ? '46% 54% 62% 38% / 58% 42% 56% 44%'
      : surface.shape === 'line' ? '999px' : `${surface.radius ?? 28}px`;
  return {
    background,
    border: surface.borderStyle === 'none' || surface.borderWidth === 0
      ? 'none'
      : `${surface.borderWidth ?? 0}px ${surface.borderStyle} ${resolveColorValue(palette, surface.borderColor, surface.borderCustomColor, 'light')}`,
    borderRadius: radius,
    opacity: String((surface.opacity ?? 100) / 100),
    boxShadow: shadowValue(surface.shadow ?? 'none'),
    transform: surface.rotation ? `rotate(${surface.rotation}deg)` : 'none',
  };
}

export function hoverIsMotion(hover: HoverEffect): boolean {
  return hover === 'lift' || hover === 'scale' || hover === 'zoom';
}

export function effectiveHover(hover: HoverEffect, prefersReducedMotion: boolean): HoverEffect {
  return prefersReducedMotion && hoverIsMotion(hover) ? 'none' : hover;
}

// ---------------------------------------------------------------------------
// Dividers and decorations
// ---------------------------------------------------------------------------

const DIVIDER_HEIGHTS: Record<string, string> = {
  sm: 'clamp(28px, 4vw, 52px)',
  md: 'clamp(44px, 6.5vw, 88px)',
  lg: 'clamp(64px, 9vw, 140px)',
};

const DIVIDER_PATHS: Record<string, { path: string; viewBox: string }> = {
  wave: { viewBox: '0 0 1200 120', path: 'M0,40 C200,110 400,0 600,40 C800,80 1000,10 1200,50 L1200,120 L0,120 Z' },
  curve: { viewBox: '0 0 1200 120', path: 'M0,0 C300,120 900,120 1200,0 L1200,120 L0,120 Z' },
  diagonal: { viewBox: '0 0 1200 120', path: 'M0,120 L1200,0 L1200,120 Z' },
  triangle: { viewBox: '0 0 1200 120', path: 'M0,0 L600,120 L1200,0 L1200,120 L0,120 Z' },
  tilt: { viewBox: '0 0 1200 120', path: 'M0,90 L1200,20 L1200,120 L0,120 Z' },
};

export interface DividerShape { path: string; viewBox: string; height: string; flip: boolean; color?: ColorRole; customColor?: string | null }

export function resolveDivider(divider: { style?: string; size?: string; flip?: boolean; color?: ColorRole; customColor?: string | null } | undefined): DividerShape | null {
  if (!divider || !divider.style || divider.style === 'none') return null;
  const shape = DIVIDER_PATHS[divider.style];
  if (!shape) return null;
  return {
    path: shape.path,
    viewBox: shape.viewBox,
    height: DIVIDER_HEIGHTS[divider.size ?? 'md'] ?? DIVIDER_HEIGHTS.md!,
    flip: divider.flip ?? false,
    color: divider.color,
    customColor: divider.customColor,
  };
}

const INTENSITY_OPACITY: Record<string, number> = { subtle: 0.28, medium: 0.5, strong: 0.78 };
const POSITION_INSETS: Record<string, string> = {
  center: '0',
  'top-left': '-30% 40% 40% -30%',
  'top-right': '-30% -30% 40% 40%',
  'bottom-left': '40% 40% -30% -30%',
  'bottom-right': '40% -30% -30% 40%',
};

export interface DecorationLayer {
  background: string;
  backgroundSize?: string;
  opacity: number;
  inset: string;
  filter?: string;
}

export function resolveDecoration(
  theme: Theme,
  decoration: { style?: string; intensity?: string; position?: string; color?: ColorRole; customColor?: string | null } | undefined,
): DecorationLayer | null {
  if (!decoration || !decoration.style || decoration.style === 'none') return null;
  const palette = resolvePalette(theme);
  const color = resolveColorValue(palette, decoration.color, decoration.customColor, 'primary');
  const opacity = INTENSITY_OPACITY[decoration.intensity ?? 'subtle'] ?? INTENSITY_OPACITY.subtle!;
  const inset = POSITION_INSETS[decoration.position ?? 'center'] ?? '0';

  switch (decoration.style) {
    case 'glow':
      return {
        background: `radial-gradient(circle at center, ${hexToRgba(color, 0.9)} 0%, ${hexToRgba(color, 0.35)} 35%, transparent 70%)`,
        opacity, inset, filter: 'blur(40px)',
      };
    case 'lines':
      return {
        background: `repeating-linear-gradient(115deg, transparent 0 38px, ${hexToRgba(color, 0.5)} 38px 39px)`,
        opacity: opacity * 0.6, inset,
      };
    case 'grid':
      return {
        background: `linear-gradient(${hexToRgba(color, 0.45)} 1px, transparent 1px), linear-gradient(90deg, ${hexToRgba(color, 0.45)} 1px, transparent 1px)`,
        backgroundSize: '44px 44px', opacity: opacity * 0.5, inset,
      };
    case 'dots':
      return {
        background: `radial-gradient(${hexToRgba(color, 0.6)} 1.5px, transparent 1.6px)`,
        backgroundSize: '26px 26px', opacity: opacity * 0.7, inset,
      };
    case 'blobs':
      return {
        background: [
          `radial-gradient(closest-side circle at 22% 28%, ${hexToRgba(color, 0.75)}, transparent)`,
          `radial-gradient(closest-side circle at 78% 66%, ${hexToRgba(palette.accent, 0.6)}, transparent)`,
        ].join(', '),
        opacity, inset, filter: 'blur(56px)',
      };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Destinations
// ---------------------------------------------------------------------------

export interface ResolvedDestination { href: string; newTab: boolean; external: boolean }

const SAFE_PROTOCOLS = new Set(['http:', 'https:']);

/**
 * Mirror of `resolveDestination`. The canvas needs this to decide whether a
 * button previews as active or inert -- an authored destination that the
 * published site will refuse must look refused here too, or the owner only
 * discovers it after publishing.
 */
export function resolveDestination(
  destination: { kind?: string; pageId?: string | null; anchor?: string | null; url?: string | null; email?: string | null; phone?: string | null; assetId?: string | null; newTab?: boolean } | null | undefined,
  context: { pathForPageId: (pageId: string) => string | null; urlForAssetId?: (assetId: string) => string | null },
): ResolvedDestination | null {
  if (!destination) return null;
  const newTab = destination.newTab === true;

  switch (destination.kind ?? 'page') {
    case 'page': {
      if (!destination.pageId) return null;
      const path = context.pathForPageId(destination.pageId);
      return path === null ? null : { href: path, newTab, external: false };
    }
    case 'anchor': {
      const anchor = (destination.anchor ?? '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
      if (!anchor) return null;
      if (destination.pageId) {
        const path = context.pathForPageId(destination.pageId);
        return path === null ? null : { href: `${path}#${anchor}`, newTab, external: false };
      }
      return { href: `#${anchor}`, newTab, external: false };
    }
    case 'url': {
      const url = destination.url?.trim();
      if (!url) return null;
      if (url.startsWith('/') || url.startsWith('#')) return { href: url, newTab, external: false };
      let parsed: URL;
      try { parsed = new URL(url); } catch { return null; }
      if (!SAFE_PROTOCOLS.has(parsed.protocol)) return null;
      return { href: parsed.toString(), newTab: true, external: true };
    }
    case 'email': {
      const email = destination.email?.trim();
      if (!email || !/^[^\s@,;:<>()[\]\\]+@[^\s@,;:<>()[\]\\]+\.[^\s@,;:<>()[\]\\]+$/.test(email)) return null;
      return { href: `mailto:${email}`, newTab: false, external: true };
    }
    case 'phone': {
      const phone = destination.phone?.trim();
      if (!phone) return null;
      const cleaned = phone.replace(/[^\d+()\-.\s]/g, '').trim();
      if (!cleaned) return null;
      return { href: `tel:${cleaned.replace(/\s+/g, '')}`, newTab: false, external: true };
    }
    case 'file': {
      if (!destination.assetId || !context.urlForAssetId) return null;
      const url = context.urlForAssetId(destination.assetId);
      return url === null ? null : { href: url, newTab: true, external: false };
    }
    default:
      return null;
  }
}

export const COLUMN_TEMPLATES: Record<ColumnLayout, string> = {
  '1': 'minmax(0, 1fr)',
  '2': 'repeat(2, minmax(0, 1fr))',
  '1-2': 'minmax(0, 1fr) minmax(0, 2fr)',
  '2-1': 'minmax(0, 2fr) minmax(0, 1fr)',
  '1-3': 'minmax(0, 1fr) minmax(0, 3fr)',
  '3-1': 'minmax(0, 3fr) minmax(0, 1fr)',
  '3': 'repeat(3, minmax(0, 1fr))',
  '4': 'repeat(4, minmax(0, 1fr))',
  '1-1-2': 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr)',
  '2-1-1': 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)',
};
